import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from './config.service';
import { ExportJob } from '../_models/export-job.model';

/**
 * Gestione lato client dei download asincroni del portale (vedi backend
 * ExportJobService / tabella cmp_export_job).
 *
 * Flusso: si avvia un export (es. startZipFatture) → la chiamata ritorna subito
 * il job in coda → l'utente e` libero di navigare. Questo service (singleton
 * root) fa polling leggero finche` ci sono job attivi e, alla conclusione,
 * mostra un toast ovunque si trovi l'utente. La lista `jobs$` alimenta il
 * pannello "I miei download". Lo stato e` sul server (per utente), quindi
 * navigando e tornando lo si ritrova.
 */
@Injectable({ providedIn: 'root' })
export class ExportJobsService {
	private readonly POLL_MS = 3000;
	/** Le chiamate di polling non devono accendere lo spinner globale. */
	private readonly noSpinner = new HttpHeaders({ 'X-No-Spinner': '1' });

	private jobsSubject = new BehaviorSubject<ExportJob[]>([]);
	readonly jobs$ = this.jobsSubject.asObservable();

	private pollSub?: Subscription;
	private companyId = 0;

	/** Id dei job gia` scaricati dall'utente (persistiti): esclusi dal badge "richiedono attenzione". */
	private readonly DOWNLOADED_KEY = 'export_jobs_downloaded';
	private downloadedIds = new Set<number>(this.loadDownloadedIds());

	constructor(private http: HttpClient, private config: ConfigService, private translate: TranslateService) {}

	/** Avvia la generazione asincrona dello ZIP PDF fatture. Ritorna il job creato (PENDING). */
	startZipFatture(companyId: number, stile: 'ade' | 'asso', fattureIds: number[], filtri: any): Observable<ExportJob> {
		this.companyId = companyId;
		let params = new HttpParams().set('companyId', String(companyId)).set('stile', stile);
		if (fattureIds && fattureIds.length > 0) {
			params = params.set('fattureIds', fattureIds.join(','));
		}
		if (filtri) {
			Object.keys(filtri).forEach((k) => {
				if (filtri[k] !== null && filtri[k] !== undefined && filtri[k] !== -1 && filtri[k] !== '') {
					params = params.set(k, filtri[k].toString());
				}
			});
		}
		return this.startExport(
			this.http.get<ExportJob>(`${this.config.companiesUrl}scadenziario-fatture/zip-fatture-pdf`, { params }),
		);
	}

	/**
	 * Download asincrono GENERICO di un file dal documentale (dato il drfl_id).
	 * Riusato da tutte le sezioni che scaricavano allegati/documenti in modo
	 * sincrono (documenti, sopralluoghi, assemblee, ...). mode 'file' = file grezzo,
	 * 'zip' = file dentro uno ZIP (come il vecchio file_dwn_zip). Il job e` legato
	 * all'azienda corrente cosi` compare nel pannello "I miei download".
	 */
	startDocumentaryDownload(drflId: number, mode: 'file' | 'zip', label: string): Observable<ExportJob> {
		const params = new HttpParams()
			.set('drflId', String(drflId))
			.set('mode', mode)
			.set('cmpId', String(this.config.getCompany_id()))
			.set('label', label || '');
		return this.startExport(
			this.http.get<ExportJob>(`${this.config.companiesUrl}documentary-download/async`, { params }),
		);
	}

	/** ZIP dei documenti scelti dalla lista Dipendenti ("Cognome Nome/Tipologia/file"). */
	startEmployeeDocsZip(cmpId: number, emIds: number[], drflIds: number[], label: string): Observable<ExportJob> {
		return this.startExport(
			this.http.post<ExportJob>(`${this.config.companiesUrl}documentary-download/employee-docs/async`, {
				cmp_id: cmpId,
				em_ids: emIds,
				drfl_ids: drflIds,
				label,
			}),
		);
	}

	/**
	 * Avvia un export asincrono generico: `start$` e` la chiamata HTTP all'endpoint
	 * della feature che CREA il job e ritorna l'ExportJob (stato PENDING). Lo
	 * registriamo nella lista "I miei download" e avviamo il polling. Qualsiasi
	 * sezione del portale puo` usarlo per i propri download lenti.
	 */
	startExport(start$: Observable<ExportJob>): Observable<ExportJob> {
		return start$.pipe(
			tap((job) => {
				this.register(job);
				if (job && job.exj_id) {
					// toast unico "Generazione avviata" per qualsiasi export del portale
					this.config.openInfoNotify(
						this.translate.instant('export_jobs.started_title'),
						this.translate.instant('export_jobs.started_msg'),
					);
				}
			}),
		);
	}

	/** Registra un job appena creato (upsert in lista + avvio polling). */
	register(job: ExportJob): void {
		if (!job) return;
		if (job.exj_company_id) this.companyId = job.exj_company_id;
		this.upsert(job);
		this.ensurePolling();
	}

	/** Carica la lista dei job dell'utente per l'azienda (all'ingresso pagina). */
	loadMyJobs(companyId: number): void {
		this.companyId = companyId;
		const params = new HttpParams().set('companyId', String(companyId));
		this.http.get<ExportJob[]>(`${this.config.companiesUrl}export-jobs`, { params, headers: this.noSpinner }).subscribe({
			next: (jobs) => {
				const next = jobs || [];
				this.pruneDownloadedIds(next);
				this.jobsSubject.next(next);
				this.ensurePolling();
			},
			error: () => {},
		});
	}

	/** Rimuove un job (e il file) dalla lista dell'utente. */
	remove(job: ExportJob): void {
		this.http.delete(`${this.config.companiesUrl}export-jobs/${job.exj_id}`, { headers: this.noSpinner }).subscribe({
			next: () => this.jobsSubject.next(this.jobsSubject.value.filter((j) => j.exj_id !== job.exj_id)),
			error: () => {},
		});
	}

	/** Quanti job sono conclusi (pronti o in errore), candidati alla pulizia. */
	completedCount(): number {
		return this.jobsSubject.value.filter((j) => j.exj_stato === 'DONE' || j.exj_stato === 'ERROR').length;
	}

	/** Rimuove in blocco tutti i job conclusi (DONE/ERROR) e i relativi file. Un solo refresh ottimistico. */
	removeCompleted(): void {
		const completed = this.jobsSubject.value.filter((j) => j.exj_stato === 'DONE' || j.exj_stato === 'ERROR');
		if (!completed.length) return;
		// aggiornamento ottimistico: tolgo subito i conclusi dalla lista
		this.jobsSubject.next(this.jobsSubject.value.filter((j) => j.exj_stato !== 'DONE' && j.exj_stato !== 'ERROR'));
		completed.forEach((j) =>
			this.http.delete(`${this.config.companiesUrl}export-jobs/${j.exj_id}`, { headers: this.noSpinner }).subscribe({ error: () => {} }),
		);
	}

	downloadUrl(job: ExportJob): string {
		return this.config.imageBaseURL + '/temp/' + job.exj_file;
	}

	download(job: ExportJob): void {
		if (job.exj_stato === 'DONE' && job.exj_file) {
			window.open(this.downloadUrl(job), '_blank');
			// scaricato → non conta piu` tra quelli "che richiedono attenzione" (badge)
			this.downloadedIds.add(job.exj_id);
			this.persistDownloadedIds();
		}
	}

	/**
	 * Conteggio per il badge: solo i job che richiedono attenzione, ovvero
	 * in corso (PENDING/RUNNING), in errore, o pronti (DONE) non ancora scaricati.
	 * I DONE gia` scaricati e i conclusi "visti" non gonfiano il numero.
	 */
	attentionCount(): number {
		return this.jobsSubject.value.filter((j) => j.exj_stato !== 'DONE' || !this.downloadedIds.has(j.exj_id)).length;
	}

	private loadDownloadedIds(): number[] {
		try {
			const raw = localStorage.getItem(this.DOWNLOADED_KEY);
			return raw ? (JSON.parse(raw) as number[]) : [];
		} catch {
			return [];
		}
	}

	private persistDownloadedIds(): void {
		try {
			localStorage.setItem(this.DOWNLOADED_KEY, JSON.stringify([...this.downloadedIds]));
		} catch {
			/* localStorage non disponibile: il badge funziona comunque in-memory */
		}
	}

	/** Mantiene `downloadedIds` allineato ai job ancora esistenti (evita crescita illimitata). */
	private pruneDownloadedIds(jobs: ExportJob[]): void {
		if (this.downloadedIds.size === 0) return;
		const present = new Set(jobs.map((j) => j.exj_id));
		let changed = false;
		for (const id of [...this.downloadedIds]) {
			if (!present.has(id)) {
				this.downloadedIds.delete(id);
				changed = true;
			}
		}
		if (changed) this.persistDownloadedIds();
	}

	private upsert(job: ExportJob): void {
		const list = this.jobsSubject.value.slice();
		const i = list.findIndex((j) => j.exj_id === job.exj_id);
		if (i >= 0) list[i] = job;
		else list.unshift(job);
		this.jobsSubject.next(list);
	}

	private hasActive(jobs: ExportJob[] = this.jobsSubject.value): boolean {
		return jobs.some((j) => j.exj_stato === 'PENDING' || j.exj_stato === 'RUNNING');
	}

	/** Avvia il polling se ci sono job attivi e non e` gia` in corso. */
	private ensurePolling(): void {
		if (!this.hasActive()) return;
		if (this.pollSub && !this.pollSub.closed) return;
		const params = new HttpParams().set('companyId', String(this.companyId));
		this.pollSub = timer(this.POLL_MS, this.POLL_MS)
			.pipe(switchMap(() => this.http.get<ExportJob[]>(`${this.config.companiesUrl}export-jobs`, { params, headers: this.noSpinner })))
			.subscribe({
				next: (jobs) => {
					const next = jobs || [];
					this.notifyTransitions(this.jobsSubject.value, next);
					this.pruneDownloadedIds(next);
					this.jobsSubject.next(next);
					if (!this.hasActive(next)) {
						this.pollSub?.unsubscribe();
					}
				},
				error: () => {},
			});
	}

	/** Toast quando un job passa a DONE/ERROR (anche se l'utente e` in altre sezioni). */
	private notifyTransitions(prev: ExportJob[], next: ExportJob[]): void {
		const prevMap = new Map(prev.map((j) => [j.exj_id, j.exj_stato]));
		for (const j of next) {
			const before = prevMap.get(j.exj_id);
			if (before && before !== j.exj_stato) {
				if (j.exj_stato === 'DONE') {
					this.config.openSuccessNotify(`${this.translate.instant('export_jobs.toast_ready')}: ${j.exj_label}`);
				} else if (j.exj_stato === 'ERROR') {
					this.config.openErrorNotify(this.translate.instant('export_jobs.toast_error'));
				}
			}
		}
	}
}
